import { useState, useEffect, useMemo } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { cn } from "@/lib/utils";
import { List } from "lucide-react";
import { useContentHover } from "@/hooks/useContentHover";

interface TocItem {
  id: string;
  text: string;
  level: number;
}

interface ArticleTableOfContentsProps {
  content: any;
  className?: string;
  /** ⚠️ ANALYTICS: Callback when TOC item is clicked */
  onTocClick?: (itemText: string, position: number) => void;
}

function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/['']/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 60);
}

/**
 * Extract H2 headings from TipTap JSON content
 */
function extractHeadings(content: any): TocItem[] {
  if (!content || !content.content) return [];

  const headings: TocItem[] = [];
  const slugCounts = new Map<string, number>();

  const traverse = (node: any) => {
    if (node.type === "heading" && node.attrs?.level === 2) {
      // Extract text from heading content
      const text = node.content
        ?.map((child: any) => child.text || "")
        .join("") || "";
      
      if (text.trim()) {
        const base = slugifyHeading(text.trim()) || "section";
        const prevCount = slugCounts.get(base) ?? 0;
        const nextCount = prevCount + 1;
        slugCounts.set(base, nextCount);

        headings.push({
          id: nextCount === 1 ? `toc-${base}` : `toc-${base}-${nextCount}`,
          text: text.trim(),
          level: 2,
        });
      }
    }

    // Recurse into children
    if (node.content && Array.isArray(node.content)) {
      node.content.forEach(traverse);
    }
  };

  traverse(content);
  return headings;
}

/**
 * Custom scroll spy hook for article headings
 */
function useArticleScrollSpy(headingIds: string[]) {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (headingIds.length === 0) return;

    let raf = 0;
    const offset = 140;

    const update = () => {
      raf = 0;

      const y = window.scrollY + offset;
      let current = headingIds[0];

      for (const id of headingIds) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= y) {
          current = id;
        }
      }

      setActiveId((prev) => (prev === current ? prev : current));
    };

    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(update);
    };

    const initialTimeout = setTimeout(update, 150);

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      clearTimeout(initialTimeout);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [headingIds.join("|")]);

  return activeId;
}

/**
 * Scroll to a heading with offset for fixed navbar
 */
function scrollToHeading(id: string) {
  const element = document.getElementById(id);
  if (element) {
    const offset = 120;
    const y = element.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
}

export default function ArticleTableOfContents({
  content,
  className,
  onTocClick,
}: ArticleTableOfContentsProps) {
  const headings = useMemo(() => extractHeadings(content), [content]);
  const headingIds = useMemo(() => headings.map((h) => h.id), [headings]);
  const activeId = useArticleScrollSpy(headingIds);
  
  // ⚠️ ANALYTICS: TOC hover tracking
  const { contentViewRef: tocViewRef, contentHoverProps: tocHoverProps } = useContentHover({
    contentId: 'article-toc',
    contentType: 'content',
    contentTitle: 'Table of Contents',
    contentSection: 'article-toc',
    minHoverDuration: 2000,
  });

  // Scroll-direction based visibility (matching Learn page pattern)
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isProgrammaticScroll, setIsProgrammaticScroll] = useState(false);
  
  const { scrollY } = useScroll();
  
  useMotionValueEvent(scrollY, "change", (latest) => {
    // Ignore scroll events during programmatic scrolling (TOC click)
    if (isProgrammaticScroll) return;
    
    const scrollDelta = latest - lastScrollY;
    
    // Always show when at the top of the page
    if (latest <= 50) {
      setIsVisible(true);
    }
    // Only hide when actively scrolling DOWN (not just any scroll)
    else if (scrollDelta > 10 && latest > 100) {
      setIsVisible(false);
    }
    // Show when scrolling up
    else if (scrollDelta < -30) {
      setIsVisible(true);
    }
    // Note: No else clause - if already visible (from hover or scroll-up), stay visible
    
    // Only update lastScrollY if we've moved significantly (reduces jitter)
    if (Math.abs(scrollDelta) > 5) {
      setLastScrollY(latest);
    }
  });
  
  // Handle TOC item click - scroll without hiding
  const handleTocClick = (id: string, index: number) => {
    setIsProgrammaticScroll(true);
    setIsVisible(true);
    
    // ⚠️ ANALYTICS: Track TOC click
    const heading = headings.find(h => h.id === id);
    if (heading && onTocClick) {
      onTocClick(heading.text, index + 1);
    }
    
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      const y = element.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
    
    // Re-enable scroll detection after animation completes
    setTimeout(() => {
      setIsProgrammaticScroll(false);
      setLastScrollY(window.scrollY); // Reset baseline to current position
    }, 800);
  };
  
  // When hover ends, keep visible state - only scroll-down hides it
  // Merged with analytics hover tracking
  const handleMouseEnter = () => {
    setIsHovered(true);
    setIsVisible(true); // Force visible on hover
    tocHoverProps.onMouseEnter();
  };
  
  const handleMouseLeave = () => {
    setIsHovered(false);
    // Don't change isVisible here - let scroll behavior control it
    tocHoverProps.onMouseLeave();
  };

  // Ensure actual rendered H2 elements get the same IDs as the extracted TOC.
  useEffect(() => {
    if (headings.length === 0) return;

    const t = window.setTimeout(() => {
      const h2s = Array.from(
        document.querySelectorAll<HTMLElement>(".prose-article h2")
      );

      headings.forEach((h, i) => {
        const el = h2s[i];
        if (!el) return;
        el.id = h.id;
        el.style.scrollMarginTop = "140px";
      });
    }, 50);

    return () => window.clearTimeout(t);
  }, [headings]);

  // Don't render if fewer than 2 headings
  if (headings.length < 2) {
    return null;
  }

  const showFull = isVisible || isHovered;

  return (
    <div
      ref={tocHoverProps.ref}
      className={cn(
        "fixed left-0 top-1/2 -translate-y-1/2 z-40",
        "hidden 2xl:block",
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Peek tab - always visible when TOC is hidden */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ 
          opacity: showFull ? 0 : 1, 
          x: showFull ? -20 : 0,
          pointerEvents: showFull ? "none" : "auto"
        }}
        transition={{ duration: 0.2 }}
        className="absolute left-0 top-1/2 -translate-y-1/2 bg-platinum/90 backdrop-blur-sm rounded-r-lg p-2 cursor-pointer shadow-sm"
        aria-label="Show table of contents"
      >
        <List className="w-4 h-4 text-coal" />
      </motion.div>

      {/* Full TOC panel */}
      <motion.nav
        initial={{ opacity: 1, x: 0 }}
        animate={{ 
          opacity: showFull ? 1 : 0, 
          x: showFull ? 0 : -20,
          pointerEvents: showFull ? "auto" : "none"
        }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        className="w-52 rounded-r-3xl"
        style={{
          backgroundColor: "rgba(245, 245, 247, 0.85)",
          backdropFilter: "saturate(180%) blur(20px)",
        }}
        aria-label="Table of contents"
      >
        <div className="py-4 px-5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-coal mb-3">
            Contents
          </p>
          <ul className="max-h-[60vh] overflow-auto pr-1">
            {headings.map((heading, index) => (
              <li key={heading.id}>
                {/* Separator between sections */}
                {index > 0 && (
                  <div className="border-t border-coal/10 my-2" />
                )}
                <button
                  onClick={() => handleTocClick(heading.id, index)}
                  className={cn(
                    "text-left text-[12px] leading-snug transition-all duration-200 hover:text-carbon block py-0.5",
                    activeId === heading.id
                      ? "text-carbon font-semibold"
                      : "text-coal font-normal"
                  )}
                >
                  {heading.text}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </motion.nav>
    </div>
  );
}
